// OBS Browser Source entry module for the /live/overlay route.
//
// Wrapper + lazy + shell pattern (mirrors client/src/pages/LiveStreamPage.tsx):
// this file is import-cheap so index.tsx can eager-import OverlayLoadingShell
// and OverlaySilentBoundary (for the explicit route, the
// PublicCatchAllDispatcher intercept and the route's errorElement) without
// pulling the overlay body's chunk along. OverlayPage renders only inside the
// <Suspense> below.
//
// SILENT ON PURPOSE. Unlike LiveStreamPage/LiveRouteError, this route sits
// inside an OBS Browser Source composited live over a presenter's camera.
// There is no viewer to read an error and no "Reload" anyone is watching for,
// so both the loading fallback and the crash boundary render an empty,
// transparent, aria-hidden node: nothing may ever appear on air except what
// OverlayPage itself decided to draw.
//
// Spec: docs/superpowers/specs/2026-10-16-livestream-onair-overlay-design.md.
import {
  Component,
  type ErrorInfo,
  lazy,
  type ReactNode,
  Suspense,
} from "react";
import { claimRecoveryReload } from "../components/livestream/overlay/overlayRecovery";

const OverlayPage = lazy(
  () => import("../components/livestream/overlay/OverlayPage"),
);

/** How long a crashed overlay waits before reloading itself. */
const RECOVER_AFTER_MS = 15000;

/**
 * Full-viewport, fully transparent placeholder for the moment before the
 * overlay chunk has loaded, or after a crash. Exported so index.tsx reuses the
 * same node as the route's Suspense fallback, the dispatcher's copy and the
 * route's errorElement.
 */
export function OverlayLoadingShell() {
  return (
    <div
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, background: "transparent" }}
    />
  );
}

/**
 * Catches a render-time throw anywhere below it and draws nothing, the only
 * acceptable failure for a layer over a live camera. Logs once so the failure
 * is still visible in the console.
 */
export class OverlaySilentBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(err: Error, _info: ErrorInfo): void {
    console.error("[live-overlay]", err);
    // Recover on its own: inside OBS nobody is there to press Reload, so a
    // crash or a failed chunk load would otherwise leave the source blank for
    // the rest of the show. A reload is invisible (the page is transparent),
    // and it is capped so a crash on every load does not reload forever.
    if (claimRecoveryReload()) {
      window.setTimeout(() => window.location.reload(), RECOVER_AFTER_MS);
    }
  }

  render(): ReactNode {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

const LiveOverlayPage: React.FC = () => (
  <OverlaySilentBoundary>
    <Suspense fallback={<OverlayLoadingShell />}>
      <OverlayPage />
    </Suspense>
  </OverlaySilentBoundary>
);

export default LiveOverlayPage;
